import { useEffect, useState, useRef } from 'react';

import {
    AspectRatio,
    Box,
    Button,
    Card,
    Divider,
    Flex,
    Heading,
    Icon,
    Image,
    Link,
    ListItem,
    Spacer,
    Text,
    UnorderedList,
    useBreakpointValue
} from '@chakra-ui/react';

import { CopyButton } from './Misc/CopyButton';
import { DonationDisplay } from './Misc/DonationDisplay';

import { FaUserEdit } from "react-icons/fa";
import { FaMapLocationDot } from "react-icons/fa6";
import { FiExternalLink } from "react-icons/fi";
import { MdCheckCircle } from "react-icons/md";
import { MdCancel } from "react-icons/md";
import { TbLogin } from "react-icons/tb";

const RestaurantItem = ({name, websiteUrl, details, mapUrl}) => {    
    return (
        <ListItem>
            <Link color='teal.300' href={websiteUrl} isExternal>
                {name} <Icon as={FiExternalLink} top='3px' position="relative"/>
            </Link>&nbsp;&nbsp;/&nbsp;&nbsp;
            <Link color='teal.300' href={mapUrl} isExternal>
                Map <Icon as={FaMapLocationDot} top='3px' position="relative"/>
            </Link>       
            <Box fontSize="xs" display="block">({details})</Box>
        </ListItem>
    );
}

const GroceryItem = ({name, url, address, details}) => {
    const isMobile = useBreakpointValue(
        {
          "base": ()=> true,
          "xl": ()=>  false
        }
    );  
    
    return (
        <ListItem>
            <Link color='teal.300' href={url} isExternal>
                {name} <Icon as={FaMapLocationDot} display={isMobile() ? "inline" : "none" } top='3px' position="relative"/>
                <Box fontSize="xs" display={isMobile() ? "block" : "inline"}>({address})</Box>
                <Icon as={FaMapLocationDot} display={isMobile() ? "none" : "inline" } ml={1} top='3px' position="relative"/>
            </Link>
            <Box fontSize="xs" display="block">({details})</Box>
        </ListItem>
    );
}

export function VenueInfo({eventConfig, changePage, sessionActive, addressAllowed, schedAllowed, regAllowed}) {

    const addressRef = useRef(0);

    const srpgNum = eventConfig["srpgNum"];
    const eventShort = eventConfig["eventShort"];
    const spectatorPrice = eventConfig["spectatorPrice"];
    const competitorPrice = eventConfig["competitorPrice"];
    const poolContrib = parseInt(competitorPrice) - parseInt(spectatorPrice);

    const isMobile = useBreakpointValue(
        {
          "base": ()=> true,
          "xl": ()=>  false
        }
    );

    const RegSpecButton = () => {
        return (
            <Button mb={4} fontSize=".75rem" isDisabled={!regAllowed} flex="1" flexBasis="175px" onClick={()=>{changePage("register")}}>
                <Icon as={FaUserEdit} boxSize={5} mr={2}/> Register For Event
            </Button>
        );
    }

    const AddressDisplay = () => {
        if (addressAllowed) {
            return (
                <Flex mb={4} flexDirection="column" flexFlow="wrap" gap={3} alignItems="center" justifyContent="space-evenly">
                    <AspectRatio flex="1 100%" maxWidth={{base: "300px", xl: "650px"}} ratio={{ base: 1, xl: 16 / 9 }}>
                        <iframe style={{borderRadius: 5}} src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3037.135264933312!2d-79.92639572518156!3d40.42800362444797!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8834f1fb581074eb%3A0x895f9217ef7b7201!2s2730%20Murray%20Ave%2C%20Pittsburgh%2C%20PA%2015217!5e0!3m2!1sen!2sus!4v1745674944340!5m2!1sen!2sus" allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade"/>
                    </AspectRatio>
                    <Card bgColor="whiteAlpha.300" flex="1" flexBasis="225px" display="inline-block" alignItems="center" maxWidth="300px" padding={5}>
                        <Flex>
                            <Text ref={addressRef} fontSize=".85rem" fontWeight={700}>
                                2730 Murray Ave<br/>
                                Pittsburgh PA, 15217
                            </Text>
                            <Spacer/>
                            <CopyButton size="sm" bgColor="gray.500" copyText={addressRef.current.outerText}/>
                        </Flex>
                    </Card>
                </Flex>
            );
        }
        if (sessionActive) {
            return (
                <>
                    <Text mb={4}>
                        You will need to register for the event to get more detailed
                        address information.
                    </Text>
                    <Box textAlign="center">
                        <Button width={{base: "250px", xl: "600px"}}  mb={4} isDisabled={!regAllowed} flex="1"onClick={()=>{changePage("register")}}>
                            <Icon as={FaUserEdit} boxSize={5} mr={2}/> Register For Event
                        </Button>
                    </Box>
                </>
            );
        }
        return (
            <>
                <Text mb={4}>
                    You will need to log in and register for the event to get more detailed
                    address information.
                </Text>
                <Box textAlign="center">
                    <Button width={{base: "250px", xl: "600px"}} flex="1" mb={4} onClick={()=>{changePage("log-in")}}>
                        <Icon as={TbLogin} boxSize={6} mr={2}/> GrooveStats Log In
                    </Button>
                </Box>
            </>
        );
    }

    const ParkingDoorbellDisplay = () => {
        if (addressAllowed) {
            return(
                <>
                    <Card padding={isMobile() ? 3 : 8} mb={4}>
                        <Heading size="md" mb={4}>Parking</Heading>
                        <Divider mb={4}/>
                        <Text mb={4}>
                            There's a few places around the space that we recommend.  Please refer to the image below to see 
                            where you can park.
                        </Text>                    
                        <Image borderRadius={5} ml="auto" mr="auto" maxWidth={{base: "300px", xl: "650px"}} src={process.env.PUBLIC_URL + '/images/pgh-parking.png'} />
                    </Card>
                    <Card padding={isMobile() ? 3 : 8} mb={4}>
                        <Heading size="md" mb={4}>Doorbell</Heading>
                        <Divider mb={4}/>
                        <Text mb={4}>
                            Please ring the doorbell to be let into the building.
                        </Text>
                        <Text mb={4}>
                            It's okay to ring it multiple times; it's only audible from the social room in the space, so you won't be disturbing other tenants in the building.
                        </Text>                    
                        <Image borderRadius={5} ml="auto" mr="auto" maxWidth={{base: "300px", xl: "650px"}} src={process.env.PUBLIC_URL + '/images/pgh-door.png'} />
                    </Card>
                </>
            );
        }
        return([]);
    }

    try {
        return (
            <Box width="100%">
                <Card  padding={isMobile()? 3 : 8} mb={4}>
                    <Heading size="md" mb={4}>{!isMobile() ? "Pittsburgh" : "PGH"} Dance Game Space {!isMobile() ? "Overview" : ""}</Heading>
                    <Divider mb={4}/>
                    <Text mb={4}>
                        The <Link mb={4} color='teal.300' href='https://pghdancegamespace.org/' isExternal>Pittsburgh Dance Game Space<Icon as={FiExternalLink} mx='2px' top='3px' position="relative"/></Link> has three distinct rooms of equal size — a room with arcade
                        machines, a dedicated hangout room, and a third room for additional setups and the Stamina Radio LIVE Rave!
                    </Text>
                    <Text mb={4}>
                        Here's a YouTube playlist of videos featuring the space:
                    </Text>
                    <Text mb={4}>
                        <Link mb={4} color='teal.300' href='https://www.youtube.com/playlist?list=PLCRiQP0uxxpang1WCi838iqLZV5wk3JmK' isExternal>
                            Pittsburgh Dance Game Space Videos<Icon as={FiExternalLink} mx='2px' top='3px' position="relative"/>
                        </Link>
                    </Text>
                    <Text mb={4}>
                        It's a basement unit underneath 2 businesses.  There's stable concrete ground, the ceilings are at least 8',
                        there's no issues with noise reaching the upstairs businesses, and there's still windows to let daylight in. 
                        For dance games, it's nearly ideal.
                    </Text>
                    <Text mb={4}>
                        The PGH Dance Game Space will be open daily from 11am until (at least) 12 midnight during the event.
                    </Text>
                </Card>
                <Card  padding={isMobile()? 3 : 8} mb={4}>
                    <Heading size="md" mb={4}>Fees</Heading>
                    <Divider mb={4}/>
                    <Text mb={4}>
                        It's <strong>${spectatorPrice}.00</strong> to register as a spectator and <strong>${competitorPrice}.00</strong> to register as a competitor.
                        Apart from the extra ${poolContrib}.00 competitors pay (which goes to the prize pool), all of the money from
                        registering will go directly towards maintaining the space.
                    </Text>
                    <DonationDisplay/>
                </Card>
                <Card  padding={isMobile()? 3 : 8} mb={4}>
                    <Heading size="md" mb={4}>Rules/Conduct</Heading>
                    <Divider mb={4}/>
                    <Text mb={4}>
                        I'm generally fairly laid back when it comes to policing behavior, and this is meant to be a fun environment,
                        but obviously there needs to be some ground rules laid out for attendees.  If you have concerns about any of
                        this before or during the event, feel free to reach out to me via DMs (@archiveflax on Discord) or in person.
                    </Text>
                    <Text mb={4}>The rules are as follows:</Text>
                    <UnorderedList spacing={4} ml={8} mb={4}>
                        <ListItem>
                            <Heading size="md">Harassment of any kind will not be tolerated.</Heading>
                            This is meant to be an enjoyable weekend.  Even if you're just saying something just to be funny, remember you're in mixed
                            company and even if your friends aren't bothered by an off-color joke, there may be other people at the
                            event who are.  Please don't waste my time making me mediate these types of issues.  Use your head.
                        </ListItem>
                        <ListItem>
                            <Heading size="md">Alcohol/drugs/smoking/vaping/etc. are not permitted in the venue.</Heading>
                            I feel like this should be pretty self-explanatory, but for reasons ranging from legality to it being a nuisance to
                            other attendees (and me), please don't engage in these types of behavior at the event.  Additionally, while I can't
                            and wouldn't try to control what you do back at your room,  please don't show up to the space under the influence and 
                            create problems for everyone as a result.  
                        </ListItem>
                        <ListItem>
                            <Heading size="md">Don't mod pads unless you're permitted to.</Heading>
                            Certain setups at the event have pads that you'll be allowed to mod.  Others don't.  Be mindful of this when scheduling.
                            If you're playing on a pad someone else brought, make sure you actually have permission to mod it before you do so.
                        </ListItem>
                        <ListItem>
                            <Heading size="md">Don't interfere with people competing.</Heading>
                            This covers some obvious scenarios like "don't walk in front of the screen" and "don't step on the pads while someone is playing",
                            but also some more subtle ones-- some people really enjoy hearing encouragement and being coached through songs, while others find
                            that sort of thing extremely distracting.  No one is reasonably going to expect you to know their preferences offhand, but it doesn't hurt
                            to ask someone if they're bothered by you cheering them on before you start doing it.
                        </ListItem>
                        <ListItem>
                            <Heading size="md">Follow Stamina RPG {srpgNum}'s rules.</Heading>
                            <Text mb={4}>
                                You'll be competing on the SRPG{srpgNum} songlist, and you'll need to abide by its rules.
                            </Text>
                            <Text mb={4}>Please consult the rules below:</Text>
                            <Link mb={4} color='teal.300' href={`https://${eventShort}.groovestats.com/`} isExternal>
                               Stamina RPG {srpgNum} Info/Rules <Icon as={FiExternalLink} mx='2px' top='3px' position="relative"/>
                            </Link>                            
                        </ListItem>                        
                    </UnorderedList>
                    <Text>
                        These rules are not all-inclusive.  Any conduct scenarios I haven't covered as deemed by venue/event staff 
                        as necessitating intervention will be dealt with appropriately.
                    </Text>
                </Card>
                <Card padding={isMobile() ? 3 : 8} mb={4}>
                    <Heading size="md" mb={4}>Address/Housing</Heading>
                    <Divider mb={4}/>
                    <AddressDisplay/>
                    <Text mb={4}>
                        The Squirrel Hill neighborhood where the space is located is residential.  If you want to stay within walking
                        distance of the space, Airbnb is your only option.
                    </Text>
                    <Text mb={4}>
                        There are hotels about a ten minute drive away in <em>The Waterfront</em> area (which is also where you can
                        find a Dave & Busters with DDR, if you somehow need more dance games while you're here).
                    </Text>
                    <Text mb={4}>    
                        Here are some options:
                    </Text>
                    <UnorderedList ml={8}>
                        <ListItem>
                            <Link color='teal.300' href='https://maps.app.goo.gl/Jo3anssEETyZLcPd6' isExternal>
                                {!isMobile() ? "Courtyard Pittsburgh West Homestead/Waterfront" : "Courtyard"} <Icon as={FaMapLocationDot} top='3px' position="relative"/>
                            </Link>
                        </ListItem> 
                        <ListItem>
                            <Link color='teal.300' href='https://maps.app.goo.gl/uaskmjiMqYwgGnZ19' isExternal>
                                {!isMobile() ? "Hampton Inn & Suites Pittsburgh/Waterfront-West Homestead" : "Hampton Inn & Suites"} <Icon as={FaMapLocationDot} top='3px' position="relative"/>
                            </Link>
                        </ListItem>  
                        <ListItem>
                            <Link color='teal.300' href='https://maps.app.goo.gl/qfzq8oEjKUwitoiw6' isExternal>
                                {!isMobile() ? "Holiday Inn Express Pittsburgh E - Waterfront Dr by IHG" : "Holiday Inn Express"} <Icon as={FaMapLocationDot} top='3px' position="relative"/>
                            </Link>
                        </ListItem>                    
                    </UnorderedList>
                </Card>
                <ParkingDoorbellDisplay/>
                <Card padding={isMobile()? 3 : 8} mb={4}>
                    <Heading size="md" mb={4}>Restrooms</Heading>
                    <Divider mb={4}/>
                    <Heading size="md"><Icon as={MdCheckCircle} color="green.200" mx='2px' top='3px' position="relative"/> DO use the basement restroom</Heading>
                    <Text mb={4}>
                        Please use the restroom in the basement unit.
                    </Text>
                    <Heading size="md"><Icon as={MdCancel} color="red.200" mx='2px' top='3px' position="relative"/> DON'T use the upstairs restroom</Heading>
                    <Text mb={4}>
                        The restroom that's at street level/immediately inside the street-level door is not a public restroom and not for us to use.   That's the private restroom for the wig salon that operates above the space.
                    </Text>                        
                </Card>
                <Card padding={isMobile()? 3 : 8} mb={4}>
                    <Heading size="md" mb={4}>Food</Heading>
                    <Divider mb={4}/>
                    <Flex flexDirection="column" flexFlow="wrap" gap={3} alignItems="start" justifyContent="space-evenly" mb={4}>
                        <Card bgColor="whiteAlpha.400" maxWidth={{base: "300px", xl: "425px"}} padding={isMobile() ? 3 : 8}>
                            <Heading size="md" mb={4}>Restaurants</Heading>
                            <Divider mb={4}/>
                            <Text mb={4}>
                                There are many restaurants within walking distance on Murray Ave and separately on Forbes Ave.
                            </Text>
                            <Text mb={4}>
                                Recommendations include:
                            </Text>
                            <UnorderedList ml={isMobile() ? 8 : 12}>
                                <RestaurantItem name="Pigeon Bagels" websiteUrl="https://pigeonbagels.com/" mapUrl="https://maps.app.goo.gl/EgHoZBKXBPXtwWgH9" details="really good bagels"/>
                                <RestaurantItem name="Allegro" websiteUrl="https://www.allegropgh.com/" mapUrl="https://maps.app.goo.gl/ZrSvoVx1FnRVHLk26" details="vegan bakery and sandwiches"/>
                                <RestaurantItem name="Uncle Sam's" websiteUrl="https://unclesamssubs.com/" mapUrl="https://maps.app.goo.gl/YusfPUfDts6tTwV47" details="cheesesteaks and subs"/>
                                <RestaurantItem name="Kiin" websiteUrl="https://www.kiinpgh.com/" mapUrl="https://maps.app.goo.gl/xmAKLeBHhbpU3zCi6" details="Thai, nice view"/>
                                <RestaurantItem name="98k" websiteUrl="https://www.98krestaurant.com/join-us/" mapUrl="https://maps.app.goo.gl/Ncxvb4DznZk51t299" details="fried chicken, not burgers"/>
                                <RestaurantItem name="Waffallonia" websiteUrl="https://www.waffallonia.com/" mapUrl="https://maps.app.goo.gl/BPwT7QNaPwZFN4g37" details="waffles, ice cream"/>
                            </UnorderedList>
                        </Card>
                        <Card bgColor="whiteAlpha.400" maxWidth={{base: "300px", xl: "425px"}} padding={isMobile() ? 3 : 8}>
                            <Heading size="md" mb={4}>Groceries</Heading>
                            <Divider mb={4}/>
                            <Text mb={4}>
                                There are a handful of grocery stores within walking distance of the space:
                            </Text>
                            <UnorderedList ml={isMobile() ? 8 : 12}>
                                <GroceryItem name="Giant Eagle" url="https://maps.app.goo.gl/MFad2S6iuj4gctAh6" address="1901 Murray Ave" details="0.5 miles, American, small"/>
                                <GroceryItem name="Giant Eagle" url="https://maps.app.goo.gl/dko8gfqY3jR4zJ2S6" address="4250 Murray Ave" details="0.5 miles, American, mid-sized"/>
                                <GroceryItem name="New Young's" url="https://maps.app.goo.gl/ASdwbt4DUTUTqaBy8" address="5813 Forward Ave" details="0.2 miles, Korean, tiny"/>
                                <GroceryItem name="Panda Supermarket" url="https://maps.app.goo.gl/vB9tRm7jydep1BHCA" address="5816 Forbes Ave" details="0.7 miles, Chinese, small"/>
                                <GroceryItem name="Murray Ave Kosher" url="https://maps.app.goo.gl/gMBUTL7o5fq7rC1k8" address="1916 Murray Ave" details="0.5 miles, Kosher, small"/>
                            </UnorderedList>
                        </Card>
                    </Flex>
                    <Text mb={4}>
                        The Waterfront (where hotels are, ~10 minutes by car) has larger grocery stores with more options, including a Giant Eagle and an ALDI.
                    </Text> 
                    <Text mb={4}>
                        The space includes some food prep equipment, including:
                    </Text>
                    <UnorderedList ml={isMobile() ? 8 : 12} mb={4}>
                        <ListItem>Refrigerator</ListItem>
                        <ListItem>Microwave</ListItem>
                        <ListItem>Toaster Oven</ListItem>
                        <ListItem>Rice Cooker</ListItem>
                        <ListItem>Hot Plate</ListItem>
                        <ListItem>Utensils</ListItem>
                    </UnorderedList>
                    <Text mb={4}>
                        You're free to use any of this while at the space, but please respect these amenities while exercising common
                        sense and attentiveness-- clean things after you're done using them, don't cause fires sticking metal things 
                        in the microwave, don't leave things in the toaster oven till they burn and the room gets filled with smoke, 
                        etc.-- all it takes is one person making a few bad decisions to ruin the event for everyone.  If you don't 
                        know what you're doing with this stuff, <strong>don't use it</strong>.  There's plenty of easy food options
                        within walking distance.
                    </Text>                   
                </Card>
            </Box>
        );
    } catch (e) {
        console.error("VenueInfo render error: ", e);
    }
}